<template>
  <div class="modal fade" id="submissionModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title">
            <i class="bx bx-upload me-2"></i>Submit Cash Collection
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3">Loading report details...</p>
          </div>

          <div v-else-if="reportData">
            <!-- Report Summary -->
            <div class="alert alert-info mb-4">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="alert-heading">
                    <i class="bx bx-info-circle me-2"></i>Collection Summary
                  </h6>
                  <p class="mb-2">
                    <strong>Report Reference:</strong> {{ reportData.report_reference }}<br>
                    <strong>Collection Date:</strong> {{ formatDate(reportData.collection_date) }}<br>
                    <strong>Total Transactions:</strong> {{ reportData.total_transactions }}
                  </p>
                </div>
                <div class="text-end">
                  <h4 class="text-primary mb-0">ETB {{ formatCurrency(reportData.total_amount_collected) }}</h4>
                  <small class="text-muted">Expected Amount</small>
                </div>
              </div>
            </div>

            <!-- Transaction Breakdown -->
            <div class="card mb-4">
              <div class="card-header">
                <h6 class="card-title mb-0">Transaction Breakdown by Payment Method</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Payment Method</th>
                      <th class="text-center">Count</th>
                      <th class="text-end">Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="method in paymentBreakdown" :key="method.method">
                      <td>
                        <i :class="getPaymentMethodIcon(method.method)" class="me-2"></i>
                        {{ method.method }}
                      </td>
                      <td class="text-center">{{ method.count }}</td>
                      <td class="text-end"><strong>ETB {{ formatCurrency(method.amount) }}</strong></td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Submission Form -->
            <form @submit.prevent="submitCollection">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label for="amount-submitted" class="form-label">
                    <strong>Amount Being Submitted <span class="text-danger">*</span></strong>
                  </label>
                  <div class="input-group">
                    <span class="input-group-text">ETB</span>
                    <input
                        type="number"
                        id="amount-submitted"
                        v-model="submissionForm.amountSubmitted"
                        class="form-control"
                        step="0.01"
                        min="0"
                        :max="reportData.total_amount_collected * 1.1"
                        required
                        @input="calculateVariance"
                    >
                  </div>
                  <small class="form-text text-muted">
                    Expected: ETB {{ formatCurrency(reportData.total_amount_collected) }}
                  </small>
                </div>

                <div class="col-md-6 mb-3">
                  <label class="form-label"><strong>Variance Analysis</strong></label>
                  <div class="p-3 border rounded" :class="varianceClass">
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Variance Amount:</span>
                      <strong>
                        {{ varianceAmount >= 0 ? '+' : '' }}ETB {{ formatCurrency(Math.abs(varianceAmount)) }}
                      </strong>
                    </div>
                    <small class="text-muted">
                      {{ varianceText }}
                    </small>
                  </div>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="deposit-reference" class="form-label">
                    <strong>Bank Deposit Reference</strong>
                  </label>
                  <input
                      type="text"
                      id="deposit-reference"
                      v-model="submissionForm.bankDepositReference"
                      class="form-control"
                      placeholder="Enter bank deposit slip number"
                  >
                </div>

                <div class="col-md-6 mb-3">
                  <label for="submission-date" class="form-label">
                    <strong>Submission Date <span class="text-danger">*</span></strong>
                  </label>
                  <input
                      type="datetime-local"
                      id="submission-date"
                      v-model="submissionForm.submissionDate"
                      class="form-control"
                      required
                  >
                </div>

                <div class="col-12 mb-3" v-if="Math.abs(varianceAmount) > 0">
                  <label for="variance-reason" class="form-label">
                    <strong>Variance Explanation <span class="text-danger">*</span></strong>
                  </label>
                  <textarea
                      id="variance-reason"
                      v-model="submissionForm.varianceReason"
                      class="form-control"
                      rows="3"
                      placeholder="Please explain the reason for the variance..."
                      required
                  ></textarea>
                </div>

                <div class="col-12 mb-3">
                  <label for="submission-notes" class="form-label">
                    <strong>Additional Notes</strong>
                  </label>
                  <textarea
                      id="submission-notes"
                      v-model="submissionForm.notes"
                      class="form-control"
                      rows="3"
                      placeholder="Any additional notes about this submission..."
                  ></textarea>
                </div>
              </div>

              <!-- Quick Amount Buttons -->
              <div class="mb-4">
                <label class="form-label"><strong>Quick Actions:</strong></label>
                <div class="d-flex gap-2 flex-wrap">
                  <button type="button" class="btn btn-outline-success btn-sm"
                          @click="setExactAmount">
                    Exact Amount (ETB {{ formatCurrency(reportData.total_amount_collected) }})
                  </button>
                  <button type="button" class="btn btn-outline-info btn-sm"
                          @click="setCashOnlyAmount">
                    Cash Only (ETB {{ formatCurrency(cashOnlyAmount) }})
                  </button>
                </div>
              </div>

              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                  <i class="bx bx-x me-1"></i>Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="submitting || !isFormValid">
                  <span v-if="submitting">
                    <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Submitting...
                  </span>
                  <span v-else>
                    <i class="bx bx-upload me-1"></i>Submit Collection
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "SubmissionModal",
  data() {
    return {
      modalInstance: null,
      loading: false,
      submitting: false,
      reportData: null,
      paymentBreakdown: [],
      submissionForm: {
        amountSubmitted: 0,
        bankDepositReference: '',
        submissionDate: '',
        varianceReason: '',
        notes: ''
      }
    };
  },

  computed: {
    varianceAmount() {
      if (!this.reportData) return 0;
      return parseFloat(this.submissionForm.amountSubmitted || 0) - parseFloat(this.reportData.total_amount_collected || 0);
    },

    varianceClass() {
      if (Math.abs(this.varianceAmount) < 0.01) return 'bg-light-success border-success';
      if (this.varianceAmount > 0) return 'bg-light-warning border-warning';
      return 'bg-light-danger border-danger';
    },

    varianceText() {
      if (Math.abs(this.varianceAmount) < 0.01) return 'Exact match - no variance';
      if (this.varianceAmount > 0) return 'Overage - amount submitted is more than expected';
      return 'Shortage - amount submitted is less than expected';
    },

    cashOnlyAmount() {
      return this.paymentBreakdown
          .filter(method => method.method.toLowerCase() === 'cash')
          .reduce((sum, method) => sum + parseFloat(method.amount || 0), 0);
    },

    isFormValid() {
      return this.submissionForm.amountSubmitted > 0 &&
          this.submissionForm.submissionDate &&
          (Math.abs(this.varianceAmount) < 0.01 || this.submissionForm.varianceReason.trim());
    }
  },

  mounted() {
    if (typeof window !== 'undefined' && window.bootstrap) {
      this.modalInstance = new window.bootstrap.Modal(document.getElementById('submissionModal'));
    }
    this.initializeForm();
  },

  methods: {
    async openModal(report) {
      this.loading = true;
      this.reportData = report;

      if (this.modalInstance) {
        this.modalInstance.show();
      }

      try {
        await this.loadReportDetails();
      } catch (error) {
        console.error('Error loading report details:', error);
      } finally {
        this.loading = false;
      }
    },

    async loadReportDetails() {
      const data = {
        "function": "getCashCollectionReportDetails",
        "reportId": this.reportData.report_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.paymentBreakdown = res.data.data.paymentBreakdown || [];
        }
      } catch (e) {
        console.error("Failed to load report details:", e);
      }
    },

    initializeForm() {
      const now = new Date();
      this.submissionForm = {
        amountSubmitted: 0,
        bankDepositReference: '',
        submissionDate: now.toISOString().slice(0, 16),
        varianceReason: '',
        notes: ''
      };
    },

    calculateVariance() {
      // Reactive calculation handled by computed property
    },

    setExactAmount() {
      this.submissionForm.amountSubmitted = this.reportData.total_amount_collected;
    },

    setCashOnlyAmount() {
      this.submissionForm.amountSubmitted = this.cashOnlyAmount;
    },

    async submitCollection() {
      if (!this.isFormValid) return;

      this.submitting = true;

      const data = {
        "function": "submitCashCollection",
        "reportId": this.reportData.report_id,
        "amountSubmitted": parseFloat(this.submissionForm.amountSubmitted),
        "bankDepositReference": this.submissionForm.bankDepositReference,
        "submissionDate": this.submissionForm.submissionDate,
        "varianceAmount": this.varianceAmount,
        "varianceType": this.getVarianceType(),
        "varianceReason": this.submissionForm.varianceReason,
        "notes": this.submissionForm.notes,
        "submittedBy": localStorage.getItem("userId") || 1
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('collection-submitted', {
            message: 'Cash collection submitted successfully',
            reportId: this.reportData.report_id
          });

          if (this.modalInstance) {
            this.modalInstance.hide();
          }
          this.resetForm();
        } else {
          this.showError(res.data.message || 'Failed to submit collection');
        }
      } catch (e) {
        this.showError('Error submitting cash collection');
      } finally {
        this.submitting = false;
      }
    },

    getVarianceType() {
      if (Math.abs(this.varianceAmount) < 0.01) return 'exact';
      return this.varianceAmount > 0 ? 'overage' : 'shortage';
    },

    getPaymentMethodIcon(method) {
      const methodLower = method.toLowerCase();
      if (methodLower.includes('cash')) return 'bx bx-money text-warning';
      if (methodLower.includes('telebirr') || methodLower.includes('mpesa') || methodLower.includes('cbe')) {
        return 'bx bx-mobile-alt text-success';
      }
      if (methodLower.includes('bank')) return 'bx bx-building-house text-primary';
      return 'bx bx-credit-card text-info';
    },

    resetForm() {
      this.initializeForm();
      this.reportData = null;
      this.paymentBreakdown = [];
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    },

    showError(message) {
      // You can implement a toast notification or alert here
      alert(message);
    }
  }
};
</script>

<style scoped>
.bg-light-success {
  background-color: rgba(40, 167, 69, 0.1);
}

.bg-light-warning {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-light-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.border-success {
  border-color: #28a745 !important;
}

.border-warning {
  border-color: #ffc107 !important;
}

.border-danger {
  border-color: #dc3545 !important;
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}
</style>